import React from 'react';
import PropTypes from 'prop-types';
import {injectIntl} from 'react-intl';
import {ICONS} from '../../lib/curo-icons.js';

/**
 * 첫 방문 웰컴 스포트라이트 — 큐로의 새 기능(AI 학습·파이썬·데이터)을 1회 안내하고 바로 이동.
 * 발견성(2026-07-15): 탭은 보이지만 "새로 생겼는지·뭐하는지" 눈치채기 어려운 갭을 메움.
 * 자체 상태(open) + localStorage로 1회만 노출. i18n = 인라인 맵(ai-tab 방식과 동일, ja-Hira→ja 폴백).
 */

const KEY = 'curoai_welcome_seen';

const MSG = {
    title: {en: 'Welcome to Curo AI!', ko: '큐로 AI에 오신 걸 환영해요!', vi: 'Chào mừng đến Curo AI!'},
    sub: {en: "Here's what you can do — tap to jump right in!", ko: '이런 걸 할 수 있어요 — 눌러서 바로 시작!', vi: 'Bạn có thể làm những điều này — nhấn để bắt đầu ngay!'},
    aiT: {en: 'AI Learning', ko: 'AI 학습', vi: 'Học AI'},
    aiD: {en: 'Train your own AI with the webcam', ko: '웹캠으로 나만의 AI를 만들어요', vi: 'Huấn luyện AI của riêng bạn bằng webcam'},
    pyT: {en: 'Python', ko: '파이썬', vi: 'Python'},
    pyD: {en: 'Code with real Python', ko: '진짜 파이썬으로 코딩해요', vi: 'Lập trình bằng Python thật'},
    dataT: {en: 'Data & Extensions', ko: '데이터 & 확장', vi: 'Dữ liệu & Tiện ích'},
    dataD: {en: 'Charts, weather and more — analyze data', ko: '차트·날씨 등으로 데이터를 분석해요', vi: 'Biểu đồ, thời tiết... phân tích dữ liệu'},
    close: {en: 'Close', ko: '닫기', vi: 'Đóng'}
};

const MSG_EXTRA = {
    "ja": {"title": "Curo AI へようこそ!", "sub": "こんなことができます — タップしてすぐ始めよう！", "aiT": "AI学習", "aiD": "ウェブカメラで自分だけのAIを作ろう", "pyT": "Python", "pyD": "本物のPythonでコーディング", "dataT": "データと拡張機能", "dataD": "グラフ・天気などでデータを分析", "close": "閉じる"},
    "zh-cn": {"title": "欢迎来到 Curo AI！", "sub": "你可以做这些 — 点击立即开始！", "aiT": "AI学习", "aiD": "用摄像头训练你自己的 AI", "pyT": "Python", "pyD": "用真正的 Python 编程", "dataT": "数据与扩展", "dataD": "用图表、天气等分析数据", "close": "关闭"},
    "zh-tw": {"title": "歡迎來到 Curo AI！", "sub": "你可以做這些 — 點擊立即開始！", "aiT": "AI學習", "aiD": "用攝影機訓練你自己的 AI", "pyT": "Python", "pyD": "用真正的 Python 程式設計", "dataT": "資料與擴充", "dataD": "用圖表、天氣等分析資料", "close": "關閉"},
    "de": {"title": "Willkommen bei Curo AI!", "sub": "Das kannst du tun — tippe und leg los!", "aiT": "AI-Lernen", "aiD": "Trainiere deine eigene AI mit der Webcam", "pyT": "Python", "pyD": "Programmiere mit echtem Python", "dataT": "Daten & Erweiterungen", "dataD": "Diagramme, Wetter und mehr — Daten analysieren", "close": "Schließen"},
    "es": {"title": "¡Bienvenido a Curo AI!", "sub": "Esto es lo que puedes hacer — ¡toca para empezar!", "aiT": "Aprendizaje AI", "aiD": "Entrena tu propia AI con la webcam", "pyT": "Python", "pyD": "Programa con Python de verdad", "dataT": "Datos y extensiones", "dataD": "Gráficos, clima y más — analiza datos", "close": "Cerrar"},
    "fr": {"title": "Bienvenue sur Curo AI !", "sub": "Voici ce que tu peux faire — touche pour commencer !", "aiT": "Apprentissage AI", "aiD": "Entraîne ta propre AI avec la webcam", "pyT": "Python", "pyD": "Programme avec du vrai Python", "dataT": "Données et extensions", "dataD": "Graphiques, météo et plus — analyse des données", "close": "Fermer"},
    "it": {"title": "Benvenuto in Curo AI!", "sub": "Ecco cosa puoi fare — tocca per iniziare!", "aiT": "Apprendimento AI", "aiD": "Addestra la tua AI con la webcam", "pyT": "Python", "pyD": "Programma con Python vero", "dataT": "Dati ed estensioni", "dataD": "Grafici, meteo e altro — analizza i dati", "close": "Chiudi"},
    "ru": {"title": "Добро пожаловать в Curo AI!", "sub": "Вот что можно делать — нажмите, чтобы начать!", "aiT": "Обучение AI", "aiD": "Обучите свой AI с помощью веб-камеры", "pyT": "Python", "pyD": "Программируйте на настоящем Python", "dataT": "Данные и расширения", "dataD": "Графики, погода и другое — анализ данных", "close": "Закрыть"},
};

class WelcomeModal extends React.Component {
    constructor (props) {
        super(props);
        // 2026-07-24: 첫 방문 자동 표시를 끔. 시작 화면(start-screen)이 그 역할을 대신한다
        // — 팝업 2개가 연달아 뜨면 학생이 둘 다 닫아버려 둘 다 무용지물(대표 지적).
        // 역할 분리: 시작화면=첫 방문 행동 유도 / 이 모달=상단바 "새 기능" 버튼으로 기능 재열람.
        this.state = {open: false};
        this.close = this.close.bind(this);
        this._reopen = this._reopen.bind(this);
    }
    // 상단바 "튜토리얼" 버튼이 커스텀 이벤트로 이 모달을 다시 열 수 있게(교실: 실수로 닫아도 재열람)
    componentDidMount () { window.addEventListener('curoai-open-welcome', this._reopen); }
    componentWillUnmount () { window.removeEventListener('curoai-open-welcome', this._reopen); }
    _reopen () { this.setState({open: true}); }
    t (key) {
        let loc = (this.props.intl && this.props.intl.locale) || 'en';
        if (loc === 'ja-Hira') loc = 'ja';
        const ex = MSG_EXTRA[loc];
        if (ex && ex[key] != null) return ex[key];
        const m = MSG[key];
        if (!m) return key;
        return m[loc] || m.en || key;
    }
    _mark () { try { localStorage.setItem(KEY, '1'); } catch (e) { /* noop */ } }
    close () { this._mark(); this.setState({open: false}); }
    go (fn) { this._mark(); this.setState({open: false}); if (fn) { try { fn(); } catch (e) { /* noop */ } } }
    render () {
        if (!this.state.open) return null;
        const items = [
            {icon: 'ai', title: this.t('aiT'), desc: this.t('aiD'), fn: this.props.onGoAi, color: '#14c8a6'},
            {icon: 'code', title: this.t('pyT'), desc: this.t('pyD'), fn: this.props.onGoPython, color: '#4c97ff'},
            {icon: 'chart', title: this.t('dataT'), desc: this.t('dataD'), fn: this.props.onGoData, color: '#ff8c1a'}
        ];
        return (
            <div style={S.overlay} onClick={this.close}>
                <div style={S.modal} onClick={e => e.stopPropagation()}>
                    <div style={S.title}>
                        <span
                            style={{color: '#ff8c1a', marginRight: 6, fontSize: 20, verticalAlign: '-0.1em', display: 'inline-flex'}}
                            dangerouslySetInnerHTML={{__html: ICONS.spark}}
                        />
                        {this.t('title')}
                    </div>
                    <div style={S.sub}>{this.t('sub')}</div>
                    <div style={S.list}>
                        {items.map((it, i) => (
                            <div
                                key={i}
                                style={S.item}
                                role="button"
                                tabIndex={0}
                                onClick={() => this.go(it.fn)}
                            >
                                <span
                                    style={Object.assign({}, S.icon, {background: it.color, color: '#fff', fontSize: 22})}
                                    dangerouslySetInnerHTML={{__html: ICONS[it.icon]}}
                                />
                                <span style={S.itemText}>
                                    <span style={S.itemTitle}>{it.title}</span>
                                    <span style={S.itemDesc}>{it.desc}</span>
                                </span>
                                <span style={S.arrow}>{'›'}</span>
                            </div>
                        ))}
                    </div>
                    <button style={S.closeBtn} onClick={this.close}>{this.t('close')}</button>
                </div>
            </div>
        );
    }
}

const S = {
    overlay: {position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999},
    modal: {background: '#fff', borderRadius: 16, padding: '26px 24px', width: 'min(440px, 92vw)',
        boxShadow: '0 12px 40px rgba(0,0,0,0.3)', fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif'},
    title: {fontSize: 21, fontWeight: 'bold', color: '#1f2540', textAlign: 'center'},
    sub: {fontSize: 13, color: '#67708a', textAlign: 'center', marginTop: 6, marginBottom: 18},
    list: {display: 'flex', flexDirection: 'column', gap: 10},
    item: {display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', border: '1px solid #e6e8ef',
        borderRadius: 12, cursor: 'pointer', background: '#fbfcfe'},
    icon: {flex: '0 0 auto', width: 42, height: 42, borderRadius: 10, display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontSize: 22},
    itemText: {display: 'flex', flexDirection: 'column', flex: '1 1 auto', minWidth: 0},
    itemTitle: {fontSize: 15, fontWeight: 'bold', color: '#1f2540'},
    itemDesc: {fontSize: 12, color: '#67708a', marginTop: 2},
    arrow: {flex: '0 0 auto', fontSize: 22, color: '#b7bdcc'},
    closeBtn: {display: 'block', margin: '18px auto 0', padding: '8px 22px', border: 'none', borderRadius: 20,
        background: '#eef0f5', color: '#4a5169', fontSize: 13, cursor: 'pointer'}
};

WelcomeModal.propTypes = {
    intl: PropTypes.object,
    onGoAi: PropTypes.func,
    onGoData: PropTypes.func,
    onGoPython: PropTypes.func
};

export default injectIntl(WelcomeModal);
